// ============================================================
// TV STORE — Zustand (Fase 3)
// Estado centralizado de toda a infraestrutura TV
// ============================================================
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { TV_PROFILES } from '@/types/tv'
import type { TVState, TVProfile, TVPlaybackStatus, TVConnectionStatus, TickerItem } from '@/types/tv'

const DEFAULT_PROFILE = TV_PROFILES[0]

export const useTVStore = create<TVState>()(
  subscribeWithSelector((set, get) => ({
    // ─── Config ─────────────────────────────────────────────
    obraId:            null,
    companyId:         null,
    profile:           DEFAULT_PROFILE,
    profileId:         DEFAULT_PROFILE.id,

    // ─── Playback ───────────────────────────────────────────
    currentSlideIndex: 0,
    playbackStatus:    'playing',
    elapsedSec:        0,
    totalCycles:       0,

    // ─── Conexão ────────────────────────────────────────────
    connectionStatus:  'connecting',
    lastConnectedAt:   null,
    isOffline:         false,
    offlineSinceMs:    null,

    // ─── Ticker ─────────────────────────────────────────────
    tickerItems: [],

    // ─── UI ─────────────────────────────────────────────────
    isFullscreen:        false,
    wakeLockActive:      false,
    showControls:        false,
    lastInteractionMs:   Date.now(),

    // ─── Setters simples ────────────────────────────────────
    setObraId:           (id) => set({ obraId: id }),
    setCompanyId:        (id) => set({ companyId: id }),
    setProfile:          (profile) => set({ profile, profileId: profile.id }),
    setProfileId:        (id) => {
      const p = TV_PROFILES.find(x => x.id === id) ?? DEFAULT_PROFILE
      set({ profile: p, profileId: id })
    },
    setPlaybackStatus:   (s) => set({ playbackStatus: s }),
    setElapsedSec:       (n) => set({ elapsedSec: n }),
    setIsFullscreen:     (v) => set({ isFullscreen: v }),
    setWakeLockActive:   (v) => set({ wakeLockActive: v }),
    setShowControls:     (v) => set({ showControls: v }),
    touchInteraction:    ()  => set({ showControls: true, lastInteractionMs: Date.now() }),

    // ─── Navegação ──────────────────────────────────────────
    nextSlide: () => {
      const { profile, currentSlideIndex } = get()
      const enabledSlides = profile.slides.filter(s => s.enabled)
      if (enabledSlides.length <= 1) return
      const next = (currentSlideIndex + 1) % enabledSlides.length
      const isWrapping = next === 0
      set({
        currentSlideIndex: next,
        elapsedSec: 0,
        playbackStatus: 'transitioning',
        totalCycles: isWrapping ? get().totalCycles + 1 : get().totalCycles,
      })
      setTimeout(() => set({ playbackStatus: 'playing' }), 700)
    },
    prevSlide: () => {
      const { profile, currentSlideIndex } = get()
      const enabledSlides = profile.slides.filter(s => s.enabled)
      if (enabledSlides.length <= 1) return
      const prev = (currentSlideIndex - 1 + enabledSlides.length) % enabledSlides.length
      set({ currentSlideIndex: prev, elapsedSec: 0, playbackStatus: 'transitioning' })
      setTimeout(() => set({ playbackStatus: 'playing' }), 700)
    },
    goToSlide: (index) => {
      set({ currentSlideIndex: index, elapsedSec: 0, playbackStatus: 'transitioning' })
      setTimeout(() => set({ playbackStatus: 'playing' }), 700)
    },
    incrementCycles: () => set(s => ({ totalCycles: s.totalCycles + 1 })),

    // ─── Elapsed ────────────────────────────────────────────
    incrementElapsed: () => {
      const { elapsedSec, profile, currentSlideIndex, playbackStatus } = get()
      if (playbackStatus !== 'playing') return
      const enabledSlides = profile.slides.filter(s => s.enabled)
      const slide = enabledSlides[currentSlideIndex]
      if (!slide || slide.durationSec === 0) return // 0 = sem rotação
      const next = elapsedSec + 1
      if (next >= slide.durationSec) {
        get().nextSlide()
      } else {
        set({ elapsedSec: next })
      }
    },
    resetElapsed: () => set({ elapsedSec: 0 }),

    // ─── Conexão ────────────────────────────────────────────
    setConnectionStatus: (s: TVConnectionStatus) => {
      const now = new Date().toISOString()
      const updates: Partial<TVState> = { connectionStatus: s }
      if (s === 'connected') {
        updates.lastConnectedAt = now
        updates.isOffline = false
        updates.offlineSinceMs = null
      } else if (s === 'offline' && !get().isOffline) {
        updates.isOffline = true
        updates.offlineSinceMs = Date.now()
      }
      set(updates as any)
    },
    setIsOffline: (v) => set({
      isOffline: v,
      offlineSinceMs: v ? Date.now() : null,
    }),

    // ─── Ticker ─────────────────────────────────────────────
    addTickerItem: (item: TickerItem) =>
      set(s => ({
        tickerItems: [item, ...s.tickerItems].slice(0, 30),
      })),
    clearTickerItems: () => set({ tickerItems: [] }),
  })),
)

// ─── Selectors ───────────────────────────────────────────────
export const selectCurrentSlide = (s: TVState) => {
  const enabled = s.profile.slides.filter(x => x.enabled)
  return enabled[s.currentSlideIndex] ?? null
}

export const selectEnabledSlides = (s: TVState) =>
  s.profile.slides.filter(x => x.enabled)

export const selectProgress = (s: TVState) => {
  const slide = selectCurrentSlide(s)
  if (!slide || slide.durationSec === 0) return 0
  return Math.min((s.elapsedSec / slide.durationSec) * 100, 100)
}
